import { Injectable } from '@angular/core';

import { WindowRefService } from '../helpers/window-ref.service';
import { DocumentRefService } from '../helpers/document-ref.service';
import { DomainService } from './domain.service';
import { Title } from '@angular/platform-browser';
import { ThemeService } from './theme.service';
import { DEFAULT_BRANDING_DATA, DEFAULT_DOMAINS, DOMAIN_ASSETS } from '../../constants/white-label';
import { catchError, take } from 'rxjs/operators';
import { DomainInfo } from './domain-info';
import { BehaviorSubject, lastValueFrom, Observable, of } from 'rxjs';

@Injectable()
export class WhiteLabelService {
    private domainInfoSubject: BehaviorSubject<DomainInfo> = new BehaviorSubject(null);

    constructor(
        private windowRefService: WindowRefService,
        private documentRefService: DocumentRefService,
        private domainService: DomainService,
        private title: Title,
        private themeService: ThemeService
    ) {}

    readonly domainInfo$ = this.domainInfoSubject.asObservable();

    async apply() {
        let domainInfo = { ...DEFAULT_BRANDING_DATA };
        const subdomain = this.windowRefService.window.location.hostname
            .split('.')
            .filter((item: string) => item !== 'www')[0];

        try {
            if (!DEFAULT_DOMAINS.includes(subdomain)) {
                domainInfo = await lastValueFrom(this.domainService.getDomainInfo(subdomain));
                this.themeService.updateTheme(domainInfo.prefix).catch(() => {});

                this.domainService
                    .getDomainAsset(domainInfo.prefix, DOMAIN_ASSETS.favicon)
                    .pipe(take(1))
                    .subscribe({
                        next: (favicon) => {
                            domainInfo.assets.favicon = favicon;
                            this.documentRefService.setElementAttribute('favicon', 'href', favicon);
                        },
                        error: () => {
                            this.documentRefService.setElementAttribute(
                                'favicon',
                                'href',
                                DEFAULT_BRANDING_DATA.assets.favicon
                            );
                        },
                    });
            } else {
                domainInfo.prefix = subdomain;
                this.documentRefService.setElementAttribute(
                    'favicon',
                    'href',
                    domainInfo.assets.favicon
                );
            }
        } catch {
            this.documentRefService.setElementAttribute(
                'favicon',
                'href',
                domainInfo.assets.favicon
            );
        }

        this.title.setTitle(domainInfo.provider);
        this.domainInfoSubject.next(domainInfo);
    }

    getDomainInfo(): DomainInfo {
        return this.domainInfoSubject.value;
    }

    getProductDarkLogo(domainInfo: DomainInfo): Observable<string> {
        return domainInfo.assets.darkLogo
            ? of(domainInfo.assets.darkLogo)
            : this.domainService
                  .getDomainAsset(domainInfo.prefix, DOMAIN_ASSETS.darkLogo)
                  .pipe(catchError(() => of(DEFAULT_BRANDING_DATA.assets.darkLogo)));
    }

    getProductLightLogo(domainInfo: DomainInfo): Observable<string> {
        return domainInfo.assets.lightLogo
            ? of(domainInfo.assets.lightLogo)
            : this.domainService
                  .getDomainAsset(domainInfo.prefix, DOMAIN_ASSETS.lightLogo)
                  .pipe(catchError(() => of(DEFAULT_BRANDING_DATA.assets.lightLogo)));
    }

    getProductLoginLogo(domainInfo: DomainInfo): Observable<string> {
        return domainInfo.assets.loginProductLogo
            ? of(domainInfo.assets.loginProductLogo)
            : this.domainService
                  .getDomainAsset(domainInfo.prefix, DOMAIN_ASSETS.lightLogo)
                  .pipe(catchError(() => of(DEFAULT_BRANDING_DATA.assets.loginProductLogo)));
    }

    getProviderLogo(domainInfo: DomainInfo): Observable<string> {
        return domainInfo.assets.providerLogo
            ? of(domainInfo.assets.providerLogo)
            : this.domainService
                  .getDomainAsset(domainInfo.prefix, DOMAIN_ASSETS.providerLogo)
                  .pipe(catchError(() => of(DEFAULT_BRANDING_DATA.assets.providerLogo)));
    }
}
